import React from "react"

import Button from "legacy/components/shared/Button"
import camelCase from "lodash/camelCase"
import { Element, Image } from "legacy/components/shared/base"
import { RHeroType } from "types/shared-components"
import { HeroForm } from "legacy/components/Redesign/Hero/HeroForm"
import RichText from "legacy/components/shared/RichText/RichText"
import { Maybe } from "types/Maybe"
import { Title } from "legacy/components/Redesign/Title/Title"

export type HeroProps = {
  data: Maybe<RHeroType>
  showForm: boolean
}

const setInlineImageWidth = (image_size: string): string => {
  let imageWidth = ""
  switch (image_size) {
    case "xlarge":
      imageWidth = "170px"
      break
    case "xxlarge":
      imageWidth = "280px"
      break
    default:
      imageWidth = "720px"
  }
  return imageWidth
}

const setTitleImageWidth = (image_size: string): string => {
  let imageWidth = ""
  switch (image_size) {
    case "xxxlarge":
      imageWidth = "720px"
      break
    case "xxlarge":
      imageWidth = "280px"
      break
    default:
      imageWidth = "160px"
  }
  return imageWidth
}

export const Hero: React.FC<HeroProps> = ({
  data,
  showForm,
}): React.ReactElement | null => {
  if (!data?.primary) return null

  const {
    background,
    background_image,
    button_text,
    button_url,
    form_name,
    form_confirmation_text,
    form_error_text,
    form_hidden_fields,
    form_submit_button_text,
    form_success_destination,
    eyebrow,
    has_form,
    hero_intro,
    hero_title,
    inline_image,
    inline_image_size,
    input_placeholder,
    input_type,
    justify_content,
    mobile_background_image,
    show_only_during_split_test,
    split_test_branch_name,
    title_image,
    title_image_size,
    title_size,
    version
  } = data.primary

  const isBranchSplitTestBranch = process.env.BRANCH === split_test_branch_name;

  if(show_only_during_split_test && !isBranchSplitTestBranch) {
    return null
  }

  const addAnalytics = (): void => {
    analytics.track("Form CTA Clicked")
  }

  const inlineImageHeight = inline_image?.dimensions?.height
  const inlineImageWidth = inline_image?.dimensions?.width

  const titleImageHeight = title_image?.dimensions?.height
  const titleImageWidth = title_image?.dimensions?.width

  const bgColor = background ? camelCase(background) : "inherit"

  const hasButtonData = button_text && button_url

  const renderForm = showForm || has_form

  const bgImage = background_image?.url ? `url("${background_image.url}") center center no-repeat` : ""
  const mobileBgImage = mobile_background_image?.url ? `url("${mobile_background_image.url}") center center no-repeat` : ""
  const contentHalfWidthVersion = version === "half width"
  const backgroundImage = mobileBgImage ? [mobileBgImage, bgImage]: bgImage

  let gridColumnNumber = "1"
  if(justify_content === "right") {
    gridColumnNumber = "3"
  } else if (justify_content === "center") {
    gridColumnNumber = "2"
  }

  return (
    <Element
      background={backgroundImage}
      backgroundSize={["cover", "cover"]}
      bg={bgColor}
      display={contentHalfWidthVersion ? "flex": "block"}
      mb="0"
      minHeight={contentHalfWidthVersion ? "640px" : "auto"}
      mt={backgroundImage ? "0" : ["lg", "xlg"]}
      mx="auto"
      pb={contentHalfWidthVersion ? ["10", "xlg"] : ["md", "xlg"]}
      position="relative"
      pt={contentHalfWidthVersion ? ["md", "xlg"] : ["md", "xlg"]}
      px="sm"
    >
      <Element 
        display="grid"
        gridTemplateColumns={contentHalfWidthVersion ? ["1fr", "1fr 1fr", "1fr 1fr 1fr"] : "1fr"}
        maxWidth="contentWidth"
        mx="auto"
      >
        <Element
          display="grid"
          gridColumn={gridColumnNumber}
          textAlign={contentHalfWidthVersion ? ["center", "left"] : "center"}>
          {title_image?.alt && title_image?.url && (
            <Element mb="lg" mx="auto" textAlign="center">
              <Image
                aspectRatio={`${titleImageWidth} / ${titleImageHeight}`}
                imageData={title_image}
                width={setTitleImageWidth(title_image_size)}
              />
            </Element>
          )}
          {eyebrow && (
            <Element
              color="offBlack"
              fontFamily="martel"
              fontSize={["13", "16"]}
              fontWeight="600"
              lineHeight={["22px", "28px"]}
              letterSpacing="wide"
              mb={["sm", "md"]}
            >
              {eyebrow}
            </Element>
          )}
          {hero_title?.text && (
            <Title
              mb={["sm", "md"]}
              title={hero_title?.text}
              titleSize={title_size || "Large Title 2"}
            />
          )}
          {hero_intro && (
            <RichText
              color="offBlack"
              display={contentHalfWidthVersion ? ["none", "block"] : "block"}
              fontFamily="martel"
              fontSize={["17", "18"]}
              fontWeight="400"
              lineHeight={["28px", "32px"]}
              mb={["md", "lg"]}
              render={hero_intro?.richText}
            />
          )}
          {hasButtonData && (
            <Element textAlign="center">
              <Button
                as="a"
                display="inline-block"
                fontSize={["16", "text.button"]}
                href={button_url.url}
                maxWidth="100%"
                minWidth="266px"
                onClick={() => addAnalytics()}
                px="15"
                py="20"
                variant={contentHalfWidthVersion ? "primaryYellow" : "primarySky"}
                width={contentHalfWidthVersion ? "100%" : "auto"}
              >
                {button_text}
              </Button>
            </Element>
          )}
          {inline_image?.alt && inline_image?.url && (
            <Element mb={["lg", "xlg"]} mx="auto" textAlign="center">
              <Image
                aspectRatio={`${inlineImageWidth} / ${inlineImageHeight}`}
                imageData={inline_image}
                width={setInlineImageWidth(inline_image_size)}
              />
            </Element>
          )}
          {renderForm && (
            <Element alignSelf={["flex-end", "auto"]}>
              <HeroForm
                buttonVariation={contentHalfWidthVersion ? "primaryYellow" : "primarySky"}
                formHiddenFields={form_hidden_fields}
                formInputType={input_type || "email"}
                formName={form_name || "hero-form"}
                formConfirmationText={
                  form_confirmation_text || "Thank you. You're on the list."
                }
                formErrorText={form_error_text || "Please enter a valid value"}
                formSubmitButtonText={form_submit_button_text || "Submit"}
                formSuccessDestination={form_success_destination}
                fullWidthInput={contentHalfWidthVersion}
                inputPlaceholder={input_placeholder}
              />
            </Element>
          )}
        </Element>
      </Element>
    </Element> 
  )
}
